import {
  useTemplateSettings,
  useUpdateTemplateSettings,
} from "@/profitability/settings/query";
import type {
  TemplateSettings,
  TemplateSettingsUpdate,
} from "@/profitability/settings/types";
import type { ProfitabilityReport } from "@/profitability/types";

export interface TemplateSettingsState {
  /** Undefined while loading. */
  settings: TemplateSettings | undefined;
  failed: boolean;
  /** Whether the person on the page may change them. */
  editable: boolean;
  /** Settles once the server answered; a refusal is already shown as a toast. */
  save: (update: TemplateSettingsUpdate) => Promise<void>;
  /** A change is being saved: a whole-list change (the rule order) waits for it. */
  saving: boolean;
  retry?: () => void;
}

/** The template's settings and the one way to change them, for its groups. */
export const useSettingsState = (
  businessId: string,
  report: Pick<ProfitabilityReport, "id" | "access">,
): TemplateSettingsState => {
  const query = useTemplateSettings(businessId, report.id);
  const update = useUpdateTemplateSettings(businessId, report.id);
  return {
    settings: query.data,
    failed: query.isError,
    editable: !!query.data?.editable && report.access !== "view",
    save: async (change) => {
      await update.mutateAsync(change);
    },
    retry: () => {
      void query.refetch();
    },
    saving: update.isPending,
  };
};
